import { useCallback, useEffect, useRef, useState } from 'react';

import { MAX_SCAN_PAGES } from 'components/Documents/Scanner/constants';
import type { ScannedPage } from 'components/Documents/Scanner/types';
import type { JScanifyInstance } from 'types/opencv';
import { canvasToJpegBlob, revokeObjectUrl } from 'utils/imageUtils';

const createPageId = () => `page-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;

export const useDocumentScanner = () => {
  const [pages, setPages] = useState<ScannedPage[]>([]);
  const captureCanvasRef = useRef<HTMLCanvasElement | null>(null);

  const getCaptureCanvas = () => {
    captureCanvasRef.current ??= document.createElement('canvas');
    return captureCanvasRef.current;
  };

  const clearPages = useCallback(() => {
    setPages((current) => {
      current.forEach((page) => revokeObjectUrl(page.previewUrl));
      return [];
    });
  }, []);

  const removePage = useCallback((index: number) => {
    setPages((current) => {
      const target = current[index];
      if (target) {
        revokeObjectUrl(target.previewUrl);
      }
      return current.filter((_, i) => i !== index);
    });
  }, []);

  const capturePage = useCallback(
    async (video: HTMLVideoElement, scanner: JScanifyInstance | null) => {
      if (pages.length >= MAX_SCAN_PAGES) {
        throw new Error('PAGE_LIMIT_REACHED');
      }

      if (!video.videoWidth || !video.videoHeight) {
        throw new Error('Camera is not ready yet');
      }

      const canvas = getCaptureCanvas();
      const context = canvas.getContext('2d');

      if (!context) {
        throw new Error('Unable to capture image');
      }

      // Match CSS object-fit: cover so capture matches the zoomed preview.
      const displayWidth = video.clientWidth || video.videoWidth;
      const displayHeight = video.clientHeight || video.videoHeight;
      const sourceRatio = video.videoWidth / video.videoHeight;
      const displayRatio = displayWidth / displayHeight;
      const crop =
        sourceRatio > displayRatio
          ? {
              sx: (video.videoWidth - video.videoHeight * displayRatio) / 2,
              sy: 0,
              sw: video.videoHeight * displayRatio,
              sh: video.videoHeight,
            }
          : {
              sx: 0,
              sy: (video.videoHeight - video.videoWidth / displayRatio) / 2,
              sw: video.videoWidth,
              sh: video.videoWidth / displayRatio,
            };

      canvas.width = Math.max(1, Math.round(crop.sw));
      canvas.height = Math.max(1, Math.round(crop.sh));
      context.drawImage(
        video,
        crop.sx,
        crop.sy,
        crop.sw,
        crop.sh,
        0,
        0,
        canvas.width,
        canvas.height
      );

      let processedCanvas: HTMLCanvasElement = canvas;

      if (scanner) {
        try {
          const corners = scanner.findDocumentCorners(canvas);
          if (corners) {
            processedCanvas = scanner.cropImage(canvas, corners);
          }
        } catch (error) {
          console.warn('Document edge detection failed, using original frame', error);
        }
      }

      const imageBlob = await canvasToJpegBlob(processedCanvas);
      const previewUrl = URL.createObjectURL(imageBlob);

      const page: ScannedPage = {
        id: createPageId(),
        previewUrl,
        imageBlob,
      };

      setPages((current) => [...current, page]);
      return page;
    },
    [pages.length]
  );

  const pagesRef = useRef(pages);
  pagesRef.current = pages;

  useEffect(
    () => () => {
      pagesRef.current.forEach((page) => revokeObjectUrl(page.previewUrl));
      captureCanvasRef.current = null;
    },
    []
  );

  return {
    pages,
    pageCount: pages.length,
    maxPages: MAX_SCAN_PAGES,
    canCaptureMore: pages.length < MAX_SCAN_PAGES,
    captureCanvasRef,
    capturePage,
    removePage,
    clearPages,
  };
};
